import { Fragment, ReactNode, useMemo } from "react";

interface EachProps<T> {
	/**
	 * 'of' is the array of elements to be rendered
	 */
	of: T[];
	/**
	 * 'render' is a function that defines how each element is rendered
	 */
	render: (item: T, index: number) => ReactNode;
	/**
	 * 'show' determines if the component should be rendered (default is true)
	 */
	show?: boolean;
	/**
	 * 'keyExtractor' is an optional function to generate unique keys for each element
	 */
	keyExtractor?: (item: T, index: number) => string | number;
	/**
	 * 'emptyComponent' is an optional component to display when there are no elements to render
	 */
	emptyComponent?: ReactNode;
	/**
	 * 'loadingComponent' is an optional component to display when data is still loading
	 */
	loadingComponent?: ReactNode;
	/**
	 * 'loadingCount' is the number of loading components to display
	 */
	loadingCount?: number;
	/**
	 * 'isLoading' indicates whether the data is still loading
	 */
	isLoading?: boolean;
}

const generateLoadingItems = (loadingComponent: ReactNode, count: number) => {
	return Array.from({ length: count }, (_, index) => (
		<Fragment key={index}>{loadingComponent}</Fragment>
	));
};

export const Each = <T,>({
	of,
	render,
	show = true,
	keyExtractor,
	emptyComponent = null,
	loadingComponent = null,
	isLoading = false,
	loadingCount = 1,
}: EachProps<T>): ReactNode | null => {
	// Memoize the rendering decision and the rendered elements
	const shouldRender = useMemo(
		() => show && of && of.length > 0 && !isLoading,
		[show, of, isLoading]
	);

	const renderedElements = useMemo(() => {
		if (!shouldRender) return null;
		return of.map((item, index) => {
			const key = keyExtractor ? keyExtractor(item, index) : index;
			return <Fragment key={key}>{render(item, index)}</Fragment>;
		});
	}, [of, render, keyExtractor, shouldRender]);

	if (isLoading) {
		return generateLoadingItems(loadingComponent, loadingCount);
	}

	if (!shouldRender) {
		return emptyComponent;
	}

	return <Fragment>{renderedElements}</Fragment>;
};

export default Each;
